import { lazy, Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePageMeta } from '../../core/usePageMeta';
import { platformHubUrl } from '../../core/config';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import ViewListIcon from '@mui/icons-material/ViewList';
import VerifiedIcon from '@mui/icons-material/Verified';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import SpaIcon from '@mui/icons-material/Spa';
import HotelIcon from '@mui/icons-material/Hotel';
import LocalPharmacyIcon from '@mui/icons-material/LocalPharmacy';
import StorefrontIcon from '@mui/icons-material/Storefront';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import { ROUTES } from '../../app/routes';
import {
  publicApi, type DirectorySearch, type JobCard, type OrganizationCard, type Paged, type PostCard, type ProductCard, type PublicHome,
} from './publicApi';
import {
  CategoryIcon, companyPath, EmptyState, ErrorState, HubSearchBar, LoadingGrid, openCompany,
  PublicLayout, usePublicLanguage,
} from './components';
import { PostCardView, useLikedPosts, useOwnPosts } from './PostCard';
import { SponsoredPlacements } from './SponsoredPlacements';
import { BusinessCard, FavouritesProvider, JobRow, ProductTile } from './ExploreCards';
import {
  CategoryPicker, categoryName, EXPLORE_TYPES, FilterChips, groupOf, TypeSwitch, type ExploreFilterKey, type ExploreFilters, type ExploreType,
} from './explore';

const ExploreMap = lazy(() => import('./ExploreMap'));

/** Items previewed per section on "All"; "Show all" switches the type. */
const PREVIEW = { biz: 8, prod: 10, job: 6, posts: 3 } as const;
/** Page size of a single-type list; "Show more" appends the next page. */
const PAGE_SIZE = 24;
/** The map loads more businesses at once so the pins cover the area. */
const MAP_PAGE_SIZE = 60;
const PRODUCT_LIMIT = 96;

const FILTER_PARAMS: Record<ExploreFilterKey, string> = { bookableOnline: 'book', hasShop: 'shop', hasJobs: 'hiring' };

function num(value: string | null): number | undefined {
  if (value == null || value === '') return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

/** Explore state lives in the URL (`?type=biz&cat=BEAUTY&view=map`) so it can be shared and survives Back. */
function useExploreState() {
  const [params, setParams] = useSearchParams();
  const state = useMemo(() => {
    const type = params.get('type');
    return {
      type: ((EXPLORE_TYPES as readonly string[]).includes(type ?? '') ? type : 'all') as ExploreType,
      category: params.get('cat')?.toUpperCase() || undefined,
      q: params.get('q') || undefined,
      city: params.get('city') || undefined,
      filters: {
        bookableOnline: params.get(FILTER_PARAMS.bookableOnline) === '1',
        hasShop: params.get(FILTER_PARAMS.hasShop) === '1',
        hasJobs: params.get(FILTER_PARAMS.hasJobs) === '1',
      } satisfies ExploreFilters,
      lat: num(params.get('lat')),
      lng: num(params.get('lng')),
      sort: (params.get('sort') as DirectorySearch['sort']) || undefined,
      map: params.get('view') === 'map',
    };
  }, [params]);
  const update = useCallback((changes: Record<string, string | null | undefined>) => {
    setParams((current) => {
      const next = new URLSearchParams(current);
      for (const [key, value] of Object.entries(changes)) {
        if (value) next.set(key, value); else next.delete(key);
      }
      return next;
    });
  }, [setParams]);
  return [state, update] as const;
}

/** Phone layout (matches the Hub's 600 px bottom-navigation breakpoint). */
function useNarrow() {
  const query = '(max-width: 600px)';
  const [narrow, setNarrow] = useState(() => window.matchMedia?.(query).matches ?? false);
  useEffect(() => {
    const media = window.matchMedia?.(query);
    if (!media) return undefined;
    const change = () => setNarrow(media.matches);
    change();
    media.addEventListener('change', change);
    return () => media.removeEventListener('change', change);
  }, []);
  return narrow;
}

/** A paged list that resets when `key` changes and appends on `more()`. `key === null` skips loading. */
function usePaged<T>(key: string | null, load: (page: number) => Promise<Paged<T>>) {
  const [pageState, setPageState] = useState({ key, page: 1 });
  const page = pageState.key === key ? pageState.page : 1;
  const [items, setItems] = useState<T[] | null>(null);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    if (key === null) return undefined;
    let active = true;
    setError(false);
    setBusy(true);
    if (page === 1) setItems(null);
    loadRef.current(page)
      .then((data) => {
        if (!active) return;
        setTotal(data.total);
        setItems((current) => (page === 1 ? data.items : [...(current ?? []), ...data.items]));
      })
      .catch(() => { if (active) setError(true); })
      .finally(() => { if (active) setBusy(false); });
    return () => { active = false; };
  }, [key, page, attempt]);

  return {
    items: key === null ? null : items,
    total,
    error,
    busy,
    hasMore: items !== null && items.length < total,
    more: () => setPageState({ key, page: page + 1 }),
    retry: () => setAttempt((value) => value + 1),
  };
}

/**
 * Hub landing at `/`: one explore page for businesses, products and jobs.
 *
 * "All" is a page of sections (businesses, products, jobs, categories, posts) you scroll down;
 * Businesses / Products / Jobs show one long grid with "Show more". "Show map" swaps the page for a
 * list + map of businesses. Public: reads only the anonymous `/public/*` API.
 */
/** Locale key (hub.seo.*) with the title/description of each indexed section view. */
const SEO_SECTIONS = {
  biz: { key: 'hub.seo.businesses', title: 'Businesses in Albania — restaurants, salons, hotels', description: 'The business directory of Albania: restaurants, cafés, beauty salons, barbers, hotels and apartments.' },
  prod: { key: 'hub.seo.shopProducts', title: 'Products from shops in Albania — order online', description: 'Products from shops, salons and restaurants near you. See prices and order online with one Biznesi account.' },
  job: { key: 'hub.seo.jobs', title: 'Jobs in Albania — new job offers', description: 'Job offers at restaurants, salons, hotels and businesses across Albania. Apply online.' },
} as const;

export function LandingPage() {
  const { t } = useTranslation();
  const lang = usePublicLanguage();
  const [explore, update] = useExploreState();
  const { type, category, q, city, filters, lat, lng, sort, map } = explore;
  // The three section views are indexed pages of their own (sitemap + Google sitelinks):
  // /?type=biz, /?type=prod, /?type=job. Filters (category, map, sort) keep the section's canonical;
  // a typed search or a city is not a page of its own. scripts/seo-prerender.mjs writes the same head.
  const section = SEO_SECTIONS[type as keyof typeof SEO_SECTIONS];
  usePageMeta(section ? t(`${section.key}.title`, section.title) : t('hub.seo.home.title', 'Biznesi — businesses, products and jobs in Albania'), {
    description: section ? t(`${section.key}.description`, section.description) : t('hub.seo.home.description', 'Restaurants, salons, hotels, pharmacies and more · book, order and apply for jobs'),
    fullTitle: !section,
    noindex: Boolean(q || city),
    path: section ? `/?type=${type}` : '/',
    lang,
  });

  const [home, setHome] = useState<PublicHome | null>(null);
  const [homeError, setHomeError] = useState(false);
  const [homeAttempt, setHomeAttempt] = useState(0);
  const [locating, setLocating] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setHomeError(false);
    publicApi.home(lang, undefined)
      .then((data) => { if (active) setHome(data); })
      .catch(() => { if (active) setHomeError(true); });
    return () => { active = false; };
  }, [lang, homeAttempt]);

  const groups = useMemo(() => home?.groups ?? [], [home]);
  const filtered = Boolean(q || city || category || lat != null || Object.values(filters).some(Boolean));

  // ---- businesses: preview on All, paged on Businesses, a larger batch on the map ----
  const bizSearch: DirectorySearch = useMemo(() => ({
    q, category, city, lat, lng, sort, ...filters,
    pageSize: map ? MAP_PAGE_SIZE : type === 'biz' ? PAGE_SIZE : PREVIEW.biz,
  }), [q, category, city, lat, lng, sort, filters, map, type]);
  const wantsBiz = map || type === 'all' || type === 'biz';
  const biz = usePaged<OrganizationCard>(wantsBiz ? `${lang}|${JSON.stringify(bizSearch)}` : null,
    (page) => publicApi.organizations(lang, { ...bizSearch, page }));

  // ---- jobs: the directory has no job categories, so a category hides them on All. Unsearched,
  // All shows the home payload's latest jobs instead of a second request. ----
  const jobPageSize = type === 'job' ? PAGE_SIZE : PREVIEW.job;
  const jobSearch = Boolean(q || city);
  const wantsJobs = !map && (type === 'job' || (type === 'all' && !category && jobSearch));
  const jobs = usePaged<JobCard>(wantsJobs ? `${lang}|${q}|${city}|${jobPageSize}` : null,
    (page) => publicApi.jobs(lang, { q, city, page, pageSize: jobPageSize }));

  // ---- products: one list across product lines; filtered here by group and text ----
  const [productTake, setProductTake] = useState(24);
  const [products, setProducts] = useState<ProductCard[] | null>(null);
  const wantsProducts = !map && (type === 'all' || type === 'prod');
  useEffect(() => {
    if (!wantsProducts) return undefined;
    let active = true;
    publicApi.products(lang, productTake).then((items) => { if (active) setProducts(items); }).catch(() => { if (active) setProducts([]); });
    return () => { active = false; };
  }, [lang, productTake, wantsProducts]);
  const productGroup = groupOf(groups, category)?.code;
  const shownProducts = useMemo(() => {
    const text = q?.toLowerCase();
    return (products ?? []).filter((product) => (!productGroup || product.business.groupCode === productGroup)
      && (!text || `${product.name} ${product.business.name}`.toLowerCase().includes(text))
      && (!city || product.business.city?.toLowerCase() === city.toLowerCase()));
  }, [products, productGroup, q, city]);
  const moreProducts = products !== null && products.length === productTake && productTake < PRODUCT_LIMIT;

  // ---- posts preview, only on the unfiltered All page ----
  const [posts, setPosts] = useState<PostCard[] | null>(null);
  const wantsPosts = !map && type === 'all' && !filtered;
  useEffect(() => {
    if (!wantsPosts || posts) return undefined;
    let active = true;
    publicApi.posts(lang, { pageSize: PREVIEW.posts }).then((page) => { if (active) setPosts(page.items); }).catch(() => { if (active) setPosts([]); });
    return () => { active = false; };
  }, [lang, wantsPosts, posts]);
  const liked = useLikedPosts(posts);
  const own = useOwnPosts(posts);

  const setType = (next: ExploreType) => { update({ type: next === 'all' ? null : next }); window.scrollTo({ top: 0 }); };
  const setCategory = (code: string | undefined) => update({ cat: code?.toLowerCase() });
  const toggleFilter = (key: ExploreFilterKey) => update({ [FILTER_PARAMS[key]]: filters[key] ? null : '1' });
  const clearFilters = () => update({ cat: null, book: null, shop: null, hiring: null });
  const toggleMap = () => { update({ view: map ? null : 'map' }); setSelectedId(null); window.scrollTo({ top: 0 }); };

  const locate = useCallback(() => {
    if (!('geolocation' in navigator)) {
      setLocationError(t('hub.landing.noGeolocation', 'Your browser cannot share a location.'));
      return;
    }
    setLocating(true);
    setLocationError(null);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocating(false);
        const { latitude, longitude } = position.coords;
        update({ lat: latitude.toFixed(5), lng: longitude.toFixed(5), sort: 'distance' });
      },
      () => { setLocating(false); setLocationError(t('hub.landing.locationDenied', 'Location was not shared. You can still search by city.')); },
      { timeout: 10_000, maximumAge: 300_000 },
    );
  }, [t, update]);

  // The map pane sticks under the header + explore bar; measure the bar so it fits exactly.
  const bar = useRef<HTMLDivElement>(null);
  const [barHeight, setBarHeight] = useState(0);
  useLayoutEffect(() => {
    const element = bar.current;
    if (!element) return undefined;
    const observer = new ResizeObserver(() => setBarHeight(element.offsetHeight));
    observer.observe(element);
    setBarHeight(element.offsetHeight);
    return () => observer.disconnect();
  }, []);

  const search = (value: { q: string; city: string }) => update({ q: value.q || null, city: value.city || null });
  const where = city || t('hub.explore.albania', 'Albania');
  const selectedCategory = categoryName(groups, category);
  // One search box at a time: large in the hero on "All"; otherwise in the sticky bar on wide
  // screens, or above the bar on phones so the sticky part stays one row high.
  const narrow = useNarrow();
  const hero = !map && type === 'all';
  const searchBox = <HubSearchBar large={hero} initialQuery={q ?? ''} initialCity={city ?? ''} onSearch={search} />;

  return <PublicLayout showHeaderSearch={false}><FavouritesProvider>
    <div className="hub-explore" style={{ '--hub-explore-bar': `${barHeight}px` } as React.CSSProperties}>
      {hero && <section className="hub-explore-hero">
        <HeroArt />
        <div className="hub-wrap">
          <h1>{t('hub.explore.title', 'Find places, products and work in {{where}}', { where })}</h1>
          <p>{t('hub.explore.subtitle', 'Book a table, order from local shops or apply for a job with your CV, all from one account.')}</p>
          <div className="hub-explore-hero-search">{searchBox}</div>
          <div className="hub-hero-meta">
            {home && <div className="hub-counters" aria-live="polite">
              <span><b>{home.counters.organizations}</b>{t('hub.landing.countOrganizations', 'businesses')}</span>
              <span><b>{home.counters.cities}</b>{t('hub.landing.countCities', 'cities')}</span>
              <span><b>{home.counters.openJobs}</b>{t('hub.landing.countJobs', 'open jobs')}</span>
            </div>}
            <button type="button" className="hub-btn sm ghost" onClick={locate} disabled={locating} aria-pressed={lat != null}>
              <MyLocationIcon sx={{ fontSize: 16 }} aria-hidden /> {locating ? t('hub.landing.locating', 'Locating…') : t('hub.landing.nearMe', 'Near me')}
            </button>
          </div>
          {locationError && <p className="hub-hero-note" role="status">{locationError}</p>}
        </div>
      </section>}

      {narrow && !hero && <div className="hub-wrap hub-explore-mobile-search">{searchBox}</div>}

      <div className="hub-explore-bar" ref={bar}>
        <div className="hub-wrap">
          {!narrow && !hero && <div className="hub-explore-bar-search">{searchBox}</div>}
          <div className="hub-explore-bar-row">
            <TypeSwitch value={type} onChange={setType} />
            <CategoryPicker groups={groups} value={category} onChange={setCategory} filters={filters} onToggleFilter={toggleFilter}
              onClear={clearFilters} disabled={type === 'job' && !map} open={sheetOpen} onOpenChange={setSheetOpen} />
            <div className="hub-explore-filters"><FilterChips filters={filters} onToggle={toggleFilter} /></div>
          </div>
        </div>
      </div>

      {map
        ? <MapView cards={biz.items} total={biz.total} error={biz.error} hasMore={biz.hasMore} busy={biz.busy} onMore={biz.more} onRetry={biz.retry}
          selectedId={selectedId} onSelect={setSelectedId} center={lat != null && lng != null ? { lat, lng } : undefined} categoryLabel={selectedCategory} />
        : <div className="hub-wrap hub-explore-body">
          {type === 'all' && <SponsoredPlacements lang={lang} />}
          {homeError && <div className="hub-section"><ErrorState message={t('hub.landing.loadError', 'The directory could not be loaded.')} onRetry={() => setHomeAttempt((value) => value + 1)} /></div>}

          {type === 'all' && <>
            <BusinessesSection title={filtered ? t('hub.explore.businesses', 'Businesses') : t('hub.landing.featured', 'Featured')}
              cards={!filtered && home && home.featured.length > 0 ? home.featured.slice(0, PREVIEW.biz) : filtered || home || homeError ? biz.items : null}
              count={biz.items ? t('hub.explore.placesCount', '{{count}} places', { count: filtered ? biz.total : home?.counters.organizations ?? biz.total }) : undefined}
              error={biz.error} onRetry={biz.retry} onAll={() => setType('biz')}
              empty={filtered ? t('hub.search.empty', 'No businesses match these filters. Try another category or city.')
                : t('hub.landing.noOrganizations', 'No businesses are listed yet. Businesses appear here once they publish their company profile.')} />

            {(products === null || shownProducts.length > 0) && <Section id="explore-products" title={t('hub.explore.fromShops', 'From local shops')}
              count={products ? t('hub.explore.productsCount', '{{count}} products', { count: shownProducts.length }) : undefined}
              onAll={() => setType('prod')}>
              {products === null ? <LoadingGrid count={5} label={t('hub.loading', 'Loading')} />
                : <div className="hub-explore-products">{shownProducts.slice(0, PREVIEW.prod).map((product) => <ProductTile key={product.id} product={product} />)}</div>}
            </Section>}

            {/* Unsearched jobs come from the home payload; its failure is already reported above. */}
            {!category && (jobSearch || !homeError) && <JobsSection jobs={jobSearch ? jobs.items : home ? home.latestJobs.slice(0, PREVIEW.job) : null}
              total={jobSearch ? jobs.total : home?.counters.openJobs ?? 0} error={jobSearch && jobs.error}
              onRetry={jobs.retry} onAll={() => setType('job')} />}

            {!category && groups.length > 0 && <Section id="explore-categories" title={t('hub.explore.browse', 'Browse by category')}>
              <div className="hub-explore-tiles">
                {groups.map((group) => <button key={group.code} type="button" className="hub-card hub-group hub-explore-tile" data-group={group.code}
                  onClick={() => { setCategory(group.code); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
                  <span className="hub-group-head">
                    <span className="hub-group-icon" aria-hidden><CategoryIcon name={group.icon} size={20} /></span>
                    <span className="title">{group.name}</span>
                  </span>
                  <span className="hub-muted">{group.leaves.slice(0, 3).map((leaf) => leaf.name).join(' · ')}</span>
                </button>)}
              </div>
            </Section>}

            {wantsPosts && posts && posts.length > 0 && <Section id="explore-posts" title={t('hub.posts.sectionTitle', 'Posts')}
              action={<Link to={ROUTES.social}>{t('hub.posts.allPosts', 'All posts →')}</Link>}>
              <div className="hub-explore-posts">
                {posts.map((post) => <PostCardView key={post.id} post={post} liked={liked.has(post.id)} own={own.has(post.id)}
                  onDeleted={(id) => setPosts((current) => current?.filter((item) => item.id !== id) ?? null)} />)}
              </div>
            </Section>}

            <div className="hub-banner">
              <div>
                <h2>{t('hub.landing.bannerTitle', 'Run your business with Biznesi')}</h2>
                <p>{t('hub.explore.bannerText', 'Get a public page, take orders and bookings, and post jobs to receive CVs.')}</p>
              </div>
              <Link className="hub-btn" to={ROUTES.forBusiness}>{t('hub.landing.bannerCta', 'List your business')}</Link>
            </div>
          </>}

          {type === 'biz' && <ListSection title={t('hub.explore.businessesIn', 'Businesses in {{where}}', { where })} subtitle={selectedCategory}
            count={biz.items ? t('hub.search.count', '{{count}} results', { count: biz.total }) : undefined}
            sort={<label className="hub-explore-sort">
              <span className="hub-sr">{t('hub.search.sort', 'Sort by')}</span>
              <select value={sort ?? 'recommended'} onChange={(event) => update({ sort: event.target.value === 'recommended' ? null : event.target.value })}>
                <option value="recommended">{t('hub.search.sortRecommended', 'Recommended')}</option>
                <option value="name">{t('hub.search.sortName', 'Name')}</option>
                {lat != null && <option value="distance">{t('hub.search.sortDistance', 'Distance')}</option>}
              </select>
            </label>}
            loading={!biz.items && !biz.error} error={biz.error} onRetry={biz.retry}
            empty={biz.items?.length === 0 ? t('hub.search.empty', 'No businesses match these filters. Try another category or city.') : null}
            hasMore={biz.hasMore} busy={biz.busy} onMore={biz.more}>
            <div className="hub-explore-cards">{biz.items?.map((card) => <BusinessCard key={card.id} card={card} />)}</div>
          </ListSection>}

          {type === 'prod' && <ListSection title={t('hub.explore.productsIn', 'Products in {{where}}', { where })} subtitle={selectedCategory}
            count={products ? t('hub.explore.productsCount', '{{count}} products', { count: shownProducts.length }) : undefined}
            loading={products === null} error={false} onRetry={() => undefined}
            empty={products && shownProducts.length === 0 ? t('hub.explore.noProducts', 'No products match. Clear the category or search to see more.') : null}
            hasMore={moreProducts} busy={false} onMore={() => setProductTake((value) => Math.min(PRODUCT_LIMIT, value + 24))}>
            <div className="hub-explore-products">{shownProducts.map((product) => <ProductTile key={product.id} product={product} />)}</div>
          </ListSection>}

          {type === 'job' && <ListSection title={t('hub.explore.jobsIn', 'Jobs in {{where}}', { where })}
            count={jobs.items ? t('hub.search.count', '{{count}} results', { count: jobs.total }) : undefined}
            loading={!jobs.items && !jobs.error} error={jobs.error} onRetry={jobs.retry}
            empty={jobs.items?.length === 0 ? t('hub.landing.noJobs', 'No open positions right now.') : null}
            hasMore={jobs.hasMore} busy={jobs.busy} onMore={jobs.more}>
            <div className="hub-explore-jobs">{jobs.items?.map((job) => <JobRow key={job.id} job={job} />)}</div>
          </ListSection>}
        </div>}

      <button type="button" className="hub-map-toggle" onClick={toggleMap} aria-pressed={map}>
        {map ? <><ViewListIcon sx={{ fontSize: 18 }} aria-hidden />{t('hub.explore.showList', 'Show list')}</>
          : <><MapOutlinedIcon sx={{ fontSize: 18 }} aria-hidden />{t('hub.search.showMap', 'Show map')}</>}
      </button>
    </div>
  </FavouritesProvider></PublicLayout>;
}

/** Category pins placed around the hero edges, clear of the centred text. */
const HERO_PINS: [string, string, typeof RestaurantIcon, string][] = [
  ['9%', '26%', RestaurantIcon, 'FOOD_DRINK'],
  ['4%', '64%', LocalPharmacyIcon, 'HEALTH'],
  ['17%', '84%', WorkOutlineIcon, 'JOBS'],
  ['90%', '22%', SpaIcon, 'BEAUTY'],
  ['95%', '58%', HotelIcon, 'STAY'],
  ['82%', '86%', StorefrontIcon, 'SHOP'],
];

/**
 * Hero backdrop: a quiet street map (boulevards, a ring road, a river and parks) with category
 * pins, drawn in theme tokens and faded out behind the headline. Decorative only.
 */
function HeroArt() {
  return <div className="hub-hero-art" aria-hidden>
    <svg viewBox="0 0 1440 520" preserveAspectRatio="xMidYMid slice" focusable="false">
      <path className="water" d="M-20 410 C 180 360 300 450 470 400 S 760 300 930 350 S 1250 470 1460 380" />
      <rect className="park" x="120" y="120" width="170" height="110" rx="18" />
      <rect className="park" x="1130" y="300" width="190" height="120" rx="18" />
      <ellipse className="park" cx="720" cy="470" rx="120" ry="40" />
      <circle className="road ring" cx="720" cy="250" r="300" />
      <path className="road" d="M-20 250 H 1460 M720 -20 V 540 M60 -20 L 520 540 M1380 -20 L 920 540" />
      <path className="lane" d="M-20 90 H 1460 M-20 170 H 1460 M-20 330 H 1460 M-20 480 H 1460 M200 -20 V 540 M380 -20 V 540 M540 -20 V 540 M900 -20 V 540 M1060 -20 V 540 M1240 -20 V 540" />
    </svg>
    {HERO_PINS.map(([left, top, Icon, group]) => <span key={group} className="hub-hero-pin" data-group={group} style={{ left, top }}>
      <Icon sx={{ fontSize: 20 }} />
    </span>)}
  </div>;
}

function Section({ id, title, count, onAll, action, children }: { id: string; title: string; count?: string; onAll?: () => void; action?: ReactNode; children: ReactNode }) {
  const { t } = useTranslation();
  return <section className="hub-section" aria-labelledby={id}>
    <div className="hub-section-head">
      <h2 id={id}>{title}{count && <small className="hub-explore-count">{count}</small>}</h2>
      {action ?? (onAll && <button type="button" className="hub-explore-all" onClick={onAll}>{t('hub.explore.showAll', 'Show all')} <ArrowForwardIcon sx={{ fontSize: 15 }} aria-hidden /></button>)}
    </div>
    {children}
  </section>;
}

function BusinessesSection({ title, cards, count, error, onRetry, onAll, empty }: {
  title: string; cards: OrganizationCard[] | null; count?: string; error: boolean; onRetry: () => void; onAll: () => void; empty: string;
}) {
  const { t } = useTranslation();
  return <Section id="explore-businesses" title={title} count={count} onAll={onAll}>
    {error && <ErrorState message={t('hub.search.loadError', 'Search results could not be loaded.')} onRetry={onRetry} />}
    {!cards && !error && <LoadingGrid count={4} label={t('hub.loading', 'Loading')} />}
    {cards && cards.length === 0 && <EmptyState>{empty}</EmptyState>}
    {cards && cards.length > 0 && <div className="hub-explore-cards">{cards.map((card) => <BusinessCard key={card.id} card={card} />)}</div>}
  </Section>;
}

function JobsSection({ jobs, total, error, onRetry, onAll }: { jobs: JobCard[] | null; total: number; error: boolean; onRetry: () => void; onAll: () => void }) {
  const { t } = useTranslation();
  return <Section id="explore-jobs" title={t('hub.explore.hiring', 'Hiring now')}
    count={jobs ? t('hub.explore.jobsCount', '{{count}} jobs', { count: total }) : undefined} onAll={onAll}>
    {error && <ErrorState message={t('hub.explore.jobsError', 'Jobs could not be loaded.')} onRetry={onRetry} />}
    {!jobs && !error && <LoadingGrid count={3} label={t('hub.loading', 'Loading')} />}
    {jobs && jobs.length === 0 && <EmptyState>{t('hub.landing.noJobs', 'No open positions right now.')}</EmptyState>}
    {jobs && jobs.length > 0 && <div className="hub-explore-jobs">{jobs.map((job) => <JobRow key={job.id} job={job} />)}</div>}
  </Section>;
}

function ListSection({ title, subtitle, count, sort, loading, error, onRetry, empty, hasMore, busy, onMore, children }: {
  title: string; subtitle?: string; count?: string; sort?: ReactNode; loading: boolean; error: boolean; onRetry: () => void;
  empty: string | null; hasMore: boolean; busy: boolean; onMore: () => void; children: ReactNode;
}) {
  const { t } = useTranslation();
  return <section className="hub-section" aria-labelledby="explore-list">
    <div className="hub-section-head">
      <h1 id="explore-list" className="hub-page-title">{title}
        {(count || subtitle) && <small className="hub-explore-count">{[count, subtitle].filter(Boolean).join(' · ')}</small>}</h1>
      {sort}
    </div>
    {error && <ErrorState message={t('hub.search.loadError', 'Search results could not be loaded.')} onRetry={onRetry} />}
    {loading && <LoadingGrid count={8} label={t('hub.loading', 'Loading')} />}
    {empty && <EmptyState>{empty}</EmptyState>}
    {!loading && !empty && children}
    {hasMore && <div className="hub-explore-more">
      <button type="button" className="hub-btn ghost" onClick={onMore} disabled={busy}>{busy ? t('hub.loading', 'Loading') : t('hub.explore.showMore', 'Show more')}</button>
    </div>}
  </section>;
}

/** List of businesses beside (or, on phones and portrait tablets, under) the map; rows and pins share a selection. */
function MapView({ cards, total, error, hasMore, busy, onMore, onRetry, selectedId, onSelect, center, categoryLabel }: {
  cards: OrganizationCard[] | null; total: number; error: boolean; hasMore: boolean; busy: boolean; onMore: () => void; onRetry: () => void;
  selectedId: string | null; onSelect: (id: string | null) => void; center?: { lat: number; lng: number }; categoryLabel?: string;
}) {
  const { t } = useTranslation();
  const list = useRef<HTMLDivElement>(null);
  const selected = cards?.find((card) => card.id === selectedId) ?? null;
  const onMap = cards?.filter((card) => card.latitude != null && card.longitude != null).length ?? 0;

  const pick = (id: string) => {
    onSelect(id);
    const row = Array.from(list.current?.querySelectorAll<HTMLElement>('[data-card]') ?? []).find((element) => element.dataset.card === id);
    row?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
  };

  return <div className="hub-explore-map">
    <div className="hub-explore-map-list" ref={list}>
      <p className="hub-muted" aria-live="polite">
        {cards ? t('hub.explore.mapCount', '{{count}} businesses · {{onMap}} on the map', { count: total, onMap }) : t('hub.loading', 'Loading')}
        {categoryLabel && ` · ${categoryLabel}`}
      </p>
      {error && <ErrorState message={t('hub.search.loadError', 'Search results could not be loaded.')} onRetry={onRetry} />}
      {!cards && !error && <LoadingGrid count={4} label={t('hub.loading', 'Loading')} />}
      {cards && cards.length === 0 && <EmptyState>{t('hub.search.empty', 'No businesses match these filters. Try another category or city.')}</EmptyState>}
      {cards?.map((card) => <MapRow key={card.id} card={card} selected={card.id === selectedId} onSelect={() => onSelect(card.id === selectedId ? null : card.id)} />)}
      {hasMore && <div className="hub-explore-more"><button type="button" className="hub-btn ghost sm" onClick={onMore} disabled={busy}>{t('hub.explore.showMore', 'Show more')}</button></div>}
    </div>
    <div className="hub-explore-map-pane">
      <Suspense fallback={<div className="hub-skeleton" role="status" aria-label={t('hub.loading', 'Loading')} />}>
        <ExploreMap cards={cards ?? []} selectedId={selectedId} onSelect={pick} center={center} />
      </Suspense>
      {selected && <div className="hub-map-card">
        <MapThumb card={selected} />
        <div className="info">
          <b>{selected.name}{selected.isVerified && <VerifiedIcon titleAccess={t('hub.card.verified', 'Verified')} sx={{ fontSize: 15, color: 'var(--hub-accent)', ml: 0.5, verticalAlign: '-2px' }} />}</b>
          <span className="hub-muted">{[selected.primaryCategory?.name, selected.city].filter(Boolean).join(' · ')}</span>
          <Link className="hub-btn sm" to={companyPath(selected.slug)} onClick={(event) => openCompany(event, selected.slug)}>
            {t('hub.card.open', 'Open')} <ArrowForwardIcon sx={{ fontSize: 15 }} aria-hidden />
          </Link>
        </div>
        <button type="button" className="close" aria-label={t('hub.explore.close', 'Close')} onClick={() => onSelect(null)}><CloseIcon sx={{ fontSize: 18 }} aria-hidden /></button>
      </div>}
    </div>
  </div>;
}

function MapThumb({ card }: { card: OrganizationCard }) {
  const image = card.coverUrl ?? card.logoUrl;
  return <span className="hub-map-thumb" aria-hidden style={image ? { backgroundImage: `url(${platformHubUrl(image)})` } : undefined}>
    {!image && card.name.slice(0, 2).toUpperCase()}
  </span>;
}

function MapRow({ card, selected, onSelect }: { card: OrganizationCard; selected: boolean; onSelect: () => void }) {
  const { t } = useTranslation();
  const facts = [
    card.hasOnlineBooking ? t('hub.card.bookOnline', 'Book online') : null,
    card.hasShop ? t('hub.card.shop', 'Shop') : null,
    card.openJobs > 0 ? t('hub.card.jobs', '{{count}} open jobs', { count: card.openJobs }) : null,
  ].filter(Boolean);
  const distance = card.distanceKm != null ? (card.distanceKm < 1 ? `${Math.round(card.distanceKm * 1000)} m` : `${card.distanceKm.toFixed(1)} km`) : null;
  return <div className={`hub-map-row${selected ? ' sel' : ''}`} data-card={card.id}>
    <button type="button" className="hub-map-row-main" aria-pressed={selected} onClick={onSelect}>
      <MapThumb card={card} />
      <span className="info">
        <b>{card.name}</b>
        <span className="hub-muted">{[card.primaryCategory?.name, card.city, distance].filter(Boolean).join(' · ')}</span>
        {facts.length > 0 && <span className="hub-card-facts">{facts.map((fact) => <span key={String(fact)}>{fact}</span>)}</span>}
      </span>
    </button>
    <Link className="hub-map-row-open" to={companyPath(card.slug)} onClick={(event) => openCompany(event, card.slug)} aria-label={`${t('hub.card.open', 'Open')} ${card.name}`}>
      <ArrowForwardIcon sx={{ fontSize: 18 }} aria-hidden />
    </Link>
  </div>;
}
